import { attachmentMessage } from "../state/attachmentMessage";
/**
 * Stream client adapter for CF.
 *
 * The original PP/arx contract:
 *   POST /api/decks/:id/send {message, attachments?}
 *     → returns { ok, data: { message_id } }
 *
 * The CF contract:
 *   POST /api/decks/:id/send {content}
 *     → returns 202 { turn_id, message_id }
 *
 * Plus PP runs `GET /chat?sid=...` as a long-lived SSE channel for
 * the agent stream. CF uses `GET /events` instead.
 *
 * Adapt both calls so the rest of the (PP-copied) frontend stays
 * unchanged.
 *
 * Uploaded document references are persisted as metadata-only [REF:file] lines.
 */

import type { SSEEvent } from '../types';
import { apiFetch } from './auth-fetch';
import type { ChatAttachment } from './client';

const API = '/api';

export async function sendMessageOnly(
  deckId: string,
  message: string,
  attachments?: string[],
  opts?: {
    intent?: 'agent'|'discussion';
    clientRequestId?: string;
    imageAttachments?: ChatAttachment[];
    slideRefs?: Array<{ deck_id: string; version: string; slide_num: number }>;
  },
): Promise<{ ok: boolean; message_id: string; turn_id: string; queued?:boolean; intent?:string }> {
  const body: {
    content: string;
    intent?: 'agent'|'discussion';
    client_request_id?: string;
    attachments?: ChatAttachment[];
    slide_refs?: Array<{ deck_id: string; version: string; slide_num: number }>;
  } = { content: attachmentMessage(message, attachments), intent:opts?.intent, client_request_id:opts?.clientRequestId ?? crypto.randomUUID() };
  if (opts?.imageAttachments && opts.imageAttachments.length > 0) {
    body.attachments = opts.imageAttachments;
  }
  if (opts?.slideRefs && opts.slideRefs.length > 0) {
    body.slide_refs = opts.slideRefs.slice(0, 5);
  }
  const res = await apiFetch(`${API}/decks/${deckId}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `send failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as { turn_id: string; message_id: string; queued?:boolean; intent?:string };
  return { ok: true, ...data };
}

export type EventChannel = 'chat' | 'activity' | 'meta';

const ACTIVITY_TYPES = new Set([
  'activity',
  'tool_use',
  'tool_result',
  'thinking',
  'agent_thinking',
  'message_delta',
]);
const META_TYPES = new Set(['connected', 'disconnected', 'warning']);

export function classifyEvent(event: SSEEvent): EventChannel {
  const type = event.type;
  if (META_TYPES.has(type)) return 'meta';
  if (ACTIVITY_TYPES.has(type)) return 'activity';
  return 'chat';
}

export function parseSSELines(text: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = text.split('\n');
  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;
    try {
      events.push(JSON.parse(line.slice(6)));
    } catch {
      // skip malformed
    }
  }
  return events;
}
